import { getMemberInfo } from "@/hooks/useMembers";
import styles from "./ManageSubscription.module.css";
import MHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MRadio from "@/components/ui/atoms/MRadio";
import MBadge from "@/components/ui/atoms/MBadge";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@/hooks/useSubscription";
import { triggerNativeIAPPurchase } from "@/utils/nativeBridge";

const NewSubscription = () => {
  const { t } = useTranslation();
  const memberInfo = getMemberInfo();
  const navigate = useNavigate();
  const { plans, loading, fetchPlans } = useSubscription();
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const PLAN_BENEFITS = [
    t("subscription.manageSubscription.benefits.familyAccess"),
    t("subscription.manageSubscription.benefits.privateStorage"),
    t("subscription.manageSubscription.benefits.storeDocuments"),
    t("subscription.manageSubscription.benefits.emergencyCards"),
    t("subscription.manageSubscription.benefits.extensiveStorage"),
  ];

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Filter out trial plans and internal plans - only show monthly and annual
  const paidPlans = plans.filter((p) => p.type !== "trial" && !p.internalPlan);

  const handleContinueToPayment = () => {
    const selectedPlan = plans.find((p) => p.id === selectedPlanId);
    if (!selectedPlan || !memberInfo) {
      console.error("No plan selected or member info missing");
      return;
    }

    const product = selectedPlan.products?.[0];
    if (!product) {
      console.error("No product found for selected plan");
      return;
    }

    const payload = {
      memberId: memberInfo.id,
      familyId: memberInfo.current_family,
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      planType: selectedPlan.type,
      productId: product.productId,
      price: product.price,
      currency: "INR", // Default to INR, can be made dynamic later
    };

    console.log("IAP Purchase Payload:", payload);

    // Trigger native IAP purchase flow
    triggerNativeIAPPurchase(payload);
  };

  useEffect(() => {
    // Set monthly plan as default selection
    const monthlyPlan = paidPlans.find((p) => p.type === "monthly");
    if (monthlyPlan) {
      setSelectedPlanId(monthlyPlan.id);
    }
  }, [paidPlans]);

  if (loading) {
    return null;
  }

  return (
    <div className="profile-page-root">
      <MHeader
        title={t("subscription.manageSubscription.title")}
        module="subscription"
        memberInfo={memberInfo}
        description={t("subscription.manageSubscription.description")}
        learnMoreContent={t("subscription.manageSubscription.learnMoreContent")}
        drawerTitle={t("subscription.manageSubscription.learnMoreTitle")}
      />

      <MSpacer mt={24} />

      <div className={styles.manageSubscription}>
        {/* What you get with a paid plan */}
        <div className={styles.benefitsCard}>
          <h2 className={styles.benefitsTitle}>
            {t("subscription.manageSubscription.benefitsTitle")}
          </h2>

          <div className={styles.benefitsList}>
            {PLAN_BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitItem}>
                <IconCircleCheckFilled
                  size={20}
                  color="#45168C"
                  className={styles.checkIcon}
                />
                <span className={styles.benefitText}>{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <MSpacer mt={32} />

        <h2 className={styles.sectionTitle}>
          {t("subscription.manageSubscription.selectPlan")}
        </h2>

        <MSpacer mt={16} />

        <div className={styles.plansContainer}>
          {paidPlans.map((plan) => (
            <div
              key={plan.id}
              className={`${styles.planCard} ${
                selectedPlanId === plan.id ? styles.selected : ""
              }`}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <div className={styles.planHeader}>
                <div className={styles.radioWrapper}>
                  <MRadio
                    checked={selectedPlanId === plan.id}
                    onChange={() => setSelectedPlanId(plan.id)}
                  />
                </div>
                <div className={styles.planContent}>
                  <div className={styles.planTitle}>{plan.name}</div>
                  {plan.type === "annual" && (
                    <MBadge className={styles.offerBadge}>
                      <span className={styles.offerText}>
                        {t("subscription.manageSubscription.offerBadge")}
                      </span>
                    </MBadge>
                  )}
                  <div className={styles.priceRow}>
                    {plan.type === "annual" && (
                      <span className={styles.discountedPrice}>₹ 2,388</span>
                    )}
                    <span className={styles.price}>
                      ₹ {plan.products[0]?.price || 0}/
                      {plan.type === "monthly"
                        ? t("subscription.manageSubscription.month")
                        : t("subscription.manageSubscription.year")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.actionButtons}>
        <MButton
          className={styles.confirmButton}
          text={t("subscription.manageSubscription.continueToPayment")}
          size="lg"
          onClick={handleContinueToPayment}
          disabled={!selectedPlanId}
        />
        <MButton
          variant="link"
          text="This isn't for me"
          size="lg"
          onClick={() => setShowDeclineModal(true)}
        />
      </div>

      {/* Decline Subscription Modal */}
      <MDialog
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        title="Thanks for your interest"
        description="If you change your mind later, you're always welcome to subscribe."
        primaryCtaText="Back to home"
        onPrimaryCta={() => {
          setShowDeclineModal(false);
          navigate("/dashboard");
        }}
      />
    </div>
  );
};

export default NewSubscription;
